/* eslint-disable react-hooks/rules-of-hooks */
/* eslint-disable @typescript-eslint/no-unused-vars */
import parse from "html-react-parser";
import React, { useEffect, useState } from "react";
import Modal from "react-modal";
import { Dictionary } from "lodash";
import useStyles from "./TextHighlighterRenderer.styles";
import { getRendererProps, RendererProps } from "../BaseRenderer";
import { MentionModel } from "../../../models/MentionModel";
import ChoiceListTextHighlighterRenderer from "./ChoiceListTextHighlighterRenderer";
import { VariableModel } from "../../../models/VariableModel";
import { ChoiceModel } from "../../../models/ChoiceModel";
import { VariableToChoiceModel } from "../../../models/VariableToChoiceModel";
import OpenEndRenderer from "./OpenEndRenderer";
import { translate } from "../../../models/utilities/translationUtilities";
import { getSettingBoolean, getSettingString } from "../../../models/utilities/settingUtilities";
import useTranslatable from "../../../hooks/useTranslatable";
import { MOBILE_WIDTH } from "../../../consts";

interface Props extends RendererProps {
    onHighlight: (e: any) => void;
    highlightedWords: Dictionary<any[]>;
    highlightedWordsByCategories: Dictionary<any[]>;
    showModal: boolean;
    mentions: { [variableName: string]: MentionModel[] };
    onReset(): void;
    onCancelModal: () => void;
    modalPositions: DOMRect;
    onOpenEndChange?(answer: any, event: any): void;
    onOpenEndBlur?(answer: any): void;
    showDeleteButton: boolean;
    onDeleteHighlight: () => void;
    selectedChoiceModels: ChoiceModel[];
    isChoiceAlreadyHighlighted: (choice: ChoiceModel, variableName: string) => boolean;
    duplicateWords: string[];
    openEndValue: { [key: string]: any };
    variables: VariableModel[];
    toggleIsCategorySelected(isSelected: boolean, enable: boolean): void;
    handleMouseDown: (e: any) => void;
    handleMouseMove: (e: any) => void;
    selectedButtons: string[];
    buttonsRef: any;
    modalRef: any;
    handleTouchStart: (e: any) => void;
    handleTouchMove: (e: any) => void;
    handleTouchEnd: (e: any) => void;
}
export default function TextHighlighterRenderer(props: Props) {
    function mapVariablesToChoices(variables: VariableModel[]): ChoiceModel[] {
        const choiceList: ChoiceModel[] = [];
        variables.forEach((variable) => {
            const choice = new VariableToChoiceModel(
                variable.elementId,
                variable.index,
                variable.name,
                variable.name,
                true,
                variable.translatedTexts,
            );
            choiceList.push(choice);
        });
        return choiceList;
    }
    const {
        highlightedWords,
        highlightedWordsByCategories,
        onHighlight,
        showModal,
        mentions,
        onReset,
        onCancelModal,
        question,
        questionnaire,
        modalPositions,
        onOpenEndBlur,
        onOpenEndChange,
        showDeleteButton,
        onDeleteHighlight,
        selectedChoiceModels,
        isChoiceAlreadyHighlighted,
        duplicateWords,
        language,
        openEndValue,
        variables,
        toggleIsCategorySelected,
        handleMouseDown,
        handleMouseMove,
        selectedButtons,
        buttonsRef,
        modalRef,
        handleTouchStart,
        handleTouchMove,
        handleTouchEnd,
    } = props;
    const questionTexts = question.variables[0].choiceList;
    const FormatTemplate = getSettingString(question, "HighlightFormat");
    let categoriesColors: string[] = getCategoriesColors();
    const classes = useStyles({
        category0Color: categoriesColors[0],
        category1Color: categoriesColors[1],
        category2Color: categoriesColors[2],
        category3Color: categoriesColors[3],
        category4Color: categoriesColors[4],
        category5Color: categoriesColors[5],
    });
    const variable = question.variables[0];
    const choiceList = mapVariablesToChoices(variables);
    const [deviceWidth] = useState<number>(window.innerWidth);
    const isMobilePreview = deviceWidth <= MOBILE_WIDTH;
    const isClickEventPropagation = true;
    const isMouseDownEventPropagation = true;
    let modalStyles;
    const responsePopUpTitle = useTranslatable(question, "ResponsePopUpTitle");
    const responsePopUpText = useTranslatable(question, "ResponsePopUpText");
    const responsePopUpDelete = useTranslatable(question, "ResponsePopUpDelete");

    if (showModal) {
        if (isMobilePreview) {
            modalStyles = {
                content: {
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    width: "72%",
                    height: showDeleteButton ? "50%" : "auto",
                    border: "1px solid #DFDFDF",
                    borderRadius: "20px",
                },
                overlay: {
                    backgroundColor: "transparent",
                },
            };
        } else {
            modalStyles = {
                content: {
                    top: `${modalPositions?.bottom}px`,
                    left: `${modalPositions?.left}px`,
                    right: "auto",
                    bottom: "auto",
                    transform: "",
                    width: "fit-content",
                    height: "auto",
                    position: "relative",
                    border: "1px solid #DFDFDF",
                    bordeRadius: "20px",
                    outline: "none",
                },
                overlay: {
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    marginRight: "-50%",
                    width: "100%",
                    height: "auto",
                    backgroundColor: "transparent",
                    overflow: "auto",
                    zIndex: 1000,
                },
            };
        }
    }

    function getCategoriesColors() {
        categoriesColors = [];
        variables.forEach((variableItem, index) => {
            categoriesColors[index] = getSettingString(variableItem, "Color");
        });
        return categoriesColors;
    }

    function getCurrentHighlightedStyle(choiceValue: string) {
        let highlightColor = "";

        variables.forEach((variableItem) => {
            highlightedWordsByCategories[variableItem.name].forEach((x) => {
                if (x.choiceModel && x.choiceModel.value === choiceValue) {
                    highlightColor = getClassName(variableItem);
                }
            });
        });
        return highlightColor;
    }

    function setStateFromMentions(choice: ChoiceModel) {
        variables.forEach((variableItem) => {
            if (
                isSelected(choice, variableItem.name) &&
                !highlightedWordsByCategories[variableItem.name].some(
                    (item) => choice === item.choiceModel,
                )
            ) {
                highlightedWordsByCategories[variableItem.name].push({
                    highlightedWord: translate(choice, "TEXT", language),
                    choiceModel: choice,
                });
                openEndValue[choice.value] = getOpenEndFromMention(choice, variableItem.name);
            }
        });
    }
    function isSelected(choice: ChoiceModel, variableName: string) {
        return props.mentions[variableName]?.some((mention) => mention.value === choice.value);
    }

    function getOpenEndFromMention(choice: ChoiceModel, variableName: string) {
        return props.mentions[variableName].filter((mention) => mention.value === choice.value)[0]
            .openEnd;
    }

    function getClassName(variableItem: VariableModel) {
        switch (variableItem.index) {
            case 0: {
                return classes.selectedCategory0;
            }
            case 1: {
                return classes.selectedCategory1;
            }
            case 2: {
                return classes.selectedCategory2;
            }
            case 3: {
                return classes.selectedCategory3;
            }
            case 4: {
                return classes.selectedCategory4;
            }
            default: {
                return classes.selectedCategory5;
            }
        }
    }

    function HighlightButton({ choiceValue, choicePositionIndex }) {
        const [hovered, setHovered] = useState(false);
        const [selected, setSelected] = useState(false);

        useEffect(() => {
            if (selectedButtons.includes(`text-highlight-choice-${choiceValue}`)) {
                setSelected(true);
            } else {
                setSelected(false);
            }
        }, [choiceValue]);

        const choice = questionTexts.find((item) => item.value === choiceValue);
        if (!choice) return null;
        const choiceText = translate(choice, "TEXT", language);
        if (!choiceText) return null;

        setStateFromMentions(choice);
        const touchEvent = typeof window !== "undefined" && "ontouchstart" in window;

        const divStyles = {
            cursor: "pointer",
            backgroundColor: hovered || selected ? "lightgrey" : "",
        };

        if (variables.length > 0 && getSettingBoolean(choice, "Highlightable")) {
            return (
                <span
                    key={`${choiceValue}-${choicePositionIndex}`}
                    id={`text-highlight-choice-${choiceValue}`}
                    data-position={choicePositionIndex}
                    onClick={touchEvent ? undefined : onHighlight}
                    onKeyPress={touchEvent ? undefined : onHighlight}
                    onMouseDown={(e) => handleMouseDown(e)}
                    onMouseEnter={(e) => {
                        setHovered(true);
                        handleMouseMove(e);
                    }}
                    onMouseLeave={() => setHovered(false)}
                    onTouchStart={handleTouchStart}
                    role="button"
                    tabIndex={0}
                    style={divStyles}
                    className={getCurrentHighlightedStyle(choice.value)}
                >
                    {choiceText}
                </span>
            );
        }

        return (
            <span
                key={`${choiceValue}-${choicePositionIndex}`}
                id={`text-highlight-choice-${choiceValue}`}
                data-position={choicePositionIndex}
                role="button"
                tabIndex={0}
                style={variables.length > 0 ? {} : { marginRight: 5 }}
            >
                {choiceText}
            </span>
        );
    }

    function parseHighlightFormat(highlightFormat) {
        let choicePositionIndex = 0;

        if (highlightFormat === null || highlightFormat === "" || highlightFormat === undefined) {
            return questionTexts.map((item) => (
                // eslint-disable-next-line react/jsx-key
                <HighlightButton
                    choiceValue={item.value}
                    choicePositionIndex={choicePositionIndex++}
                />
            ));
        }

        // eslint-disable-next-line consistent-return
        const replace = (domNode) => {
            if (domNode.attribs && domNode.name === "span" && domNode.attribs.mappedchoicecode) {
                const choiceValue = domNode.attribs.mappedchoicecode;
                return (
                    <HighlightButton
                        choiceValue={choiceValue}
                        choicePositionIndex={choicePositionIndex++}
                    />
                );
            }
        };

        return parse(highlightFormat, { replace });
    }

    function getOpenEndValue() {
        if (!selectedChoiceModels || selectedChoiceModels.length === 0) {
            return "";
        }

        const firstOpenEndValue = openEndValue[selectedChoiceModels[0].value];

        if (firstOpenEndValue === undefined) {
            return "";
        }

        for (let i = 1; i < selectedChoiceModels.length; i++) {
            if (openEndValue[selectedChoiceModels[i].value] !== firstOpenEndValue) {
                return "";
            }
        }

        return firstOpenEndValue;
    }

    return (
        <>
            <div
                ref={buttonsRef}
                className={
                    FormatTemplate === null || FormatTemplate === undefined
                        ? classes.withFormatDisableUserSelection
                        : classes.withoutFormatDisableUserSelection
                }
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
            >
                {parseHighlightFormat(FormatTemplate)}
            </div>
            {showModal && (
                <Modal
                    ref={modalRef}
                    isOpen={showModal}
                    style={modalStyles}
                    ariaHideApp={false}
                    shouldCloseOnOverlayClick
                    onRequestClose={onCancelModal}
                >
                    <label
                        style={{ fontWeight: "bold", fontFamily: "Open Sans" }}
                        dangerouslySetInnerHTML={{ __html: responsePopUpTitle }}
                    />
                    <ChoiceListTextHighlighterRenderer
                        onReset={onReset}
                        variable={variable}
                        mentions={mentions}
                        choices={choiceList}
                        highlightedWordsByCategories={highlightedWordsByCategories}
                        selectedChoiceModels={selectedChoiceModels}
                        highlightedWords={highlightedWords}
                        isChoiceAlreadyHighlighted={isChoiceAlreadyHighlighted}
                        duplicateWords={duplicateWords}
                        openEndValue={openEndValue}
                        toggleIsCategorySelected={toggleIsCategorySelected}
                        {...getRendererProps(props)}
                    />
                    <label
                        style={{ fontWeight: "bold", fontFamily: "Open Sans" }}
                        dangerouslySetInnerHTML={{ __html: responsePopUpText }}
                    />
                    <OpenEndRenderer
                        question={question}
                        questionnaire={questionnaire}
                        theme={undefined}
                        onOpenEndBlur={onOpenEndBlur}
                        value={getOpenEndValue()}
                        onOpenEndChange={(e) => onOpenEndChange(undefined, e)}
                        language={language}
                        style={{ background: "#F3F3F3", borderRadius: 2 }}
                        isClickEventPropagation={isClickEventPropagation}
                        isMouseDownEventPropagation={isMouseDownEventPropagation}
                    />
                    <div
                        style={{ flexDirection: "row", display: "flex", justifyContent: "center" }}
                    >
                        {showDeleteButton && (
                            // eslint-disable-next-line jsx-a11y/click-events-have-key-events
                            <div
                                role="button"
                                className={classes.deleteButton}
                                tabIndex={0}
                                onClick={(event) => {
                                    event.stopPropagation();
                                    onDeleteHighlight();
                                }}
                                dangerouslySetInnerHTML={{ __html: responsePopUpDelete }}
                            />
                        )}
                    </div>
                </Modal>
            )}
        </>
    );
}
